import { Input, Typography, Textarea, Label } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { TestCaseFormData } from "./testCaseFormTypes";
import { TenantVisibilitySection } from "./TenantVisibilitySection";
import { TeamContextSection } from "./TeamContextSection";
import { CriteriaSection } from "./CriteriaSection";
import { TagsSection } from "./TagsSection";
import { TurnsSection } from "./TurnsSection";
import { UserViewSection } from "./UserViewSection";

interface TestCaseFormProps {
  formData: TestCaseFormData;
  onChange: (data: TestCaseFormData) => void;
  currentTenant: string;
  rbacEnabled: boolean;
  teamOptions: Array<{ label: string; value: string }>;
  teamsLoading: boolean;
  availableCriteria?: Array<{ id?: string; name?: string; description?: string }>;
  allowTurnManagement?: boolean;
}

export const TestCaseForm: React.FC<TestCaseFormProps> = ({
  formData,
  onChange,
  currentTenant,
  rbacEnabled,
  teamOptions,
  teamsLoading,
  availableCriteria,
  allowTurnManagement = false,
}) => {
  const updateFormData = (updates: Partial<TestCaseFormData>) => {
    onChange({ ...formData, ...updates });
  };

  return (
    <Stack direction="column" gap="md">
      {/* Name */}
      <div>
        <Label htmlFor="testcase-name" className="al-mb-1 al-block">
          <Typography variant="body" size="sm" weight="medium">
            Name
          </Typography>
          <Input
            id="testcase-name"
            type="text"
            className="al-mt-1"
            value={formData.name}
            onChange={e => updateFormData({ name: e.target.value })}
          />
        </Label>
      </div>

      {/* Description */}
      <div>
        <Label htmlFor="testcase-description" className="al-mb-1 al-block">
          <Typography variant="body" size="sm" weight="medium">
            Description
          </Typography>
          <Textarea
            id="testcase-description"
            className="al-mt-1"
            rows={2}
            value={formData.description}
            onChange={e => updateFormData({ description: e.target.value })}
          />
        </Label>
      </div>

      {/* Row: Tenant Visibility and Team Context */}
      <div className="al-grid al-grid-cols-2 al-gap-4 al-items-start">
        <TenantVisibilitySection
          tenant={formData.tenant}
          currentTenant={currentTenant}
          onChange={tenant => updateFormData({ tenant })}
        />

        {rbacEnabled && (
          <TeamContextSection
            teamId={formData.team_id}
            teamOptions={teamOptions}
            teamsLoading={teamsLoading}
            onChange={team_id => updateFormData({ team_id })}
          />
        )}
      </div>

      <CriteriaSection
        criteria={formData.criteria}
        availableCriteria={availableCriteria}
        onAdd={(name, description) => {
          updateFormData({
            criteria: [
              ...formData.criteria,
              { name: name.trim(), description: description.trim() },
            ],
          });
        }}
        onRemove={index => {
          updateFormData({ criteria: formData.criteria.filter((_, i) => i !== index) });
        }}
      />

      <TagsSection tags={formData.tags} onChange={tags => updateFormData({ tags })} />

      <UserViewSection
        userView={formData.user_view}
        onChange={user_view => updateFormData({ user_view })}
      />

      <TurnsSection
        turns={formData.turns}
        allowTurnManagement={allowTurnManagement}
        onChange={turns => updateFormData({ turns })}
      />
    </Stack>
  );
};
